import { useCallback, useEffect, useState } from 'react';
import { addFavorite, listFavorites, removeFavorite, type FavoriteRow } from '../services/newsFavorites';
import { useUserStore } from '../store/userStore';
import type { NewsArticle } from '../types';

/**
 * Favoris du fil News.
 *
 * BASCULE OPTIMISTE : l'état change AVANT l'appel réseau. Un signet qui attend un aller-retour
 * Supabase pour s'allumer donne l'impression que l'appui n'a pas été pris en compte, et provoque
 * un second appui — donc un retrait immédiat de ce qu'on venait d'ajouter. En cas d'échec, l'état
 * revient exactement à ce qu'il était et l'écran affiche un message discret.
 *
 * NON CONNECTÉ N'EST PAS UNE ERREUR : la liste est simplement vide et l'onglet invite à se
 * connecter. On n'appelle pas Supabase pour se voir refuser par la RLS.
 */

/**
 * Reconstitue un article depuis la copie conservée en base.
 *
 * REPLI, PAS VÉRITÉ. Cette version ne sert que si l'article n'est pas dans le fil chargé — l'API
 * n'ayant pas de récupération par lot d'identifiants, un favori posé il y a des mois vit en page
 * sept que personne n'a ouverte. Dès que l'API dit quelque chose sur cet article, c'est elle qui
 * gagne : un titre corrigé depuis doit se voir.
 */
export function articleFromRow(row: FavoriteRow): NewsArticle {
  const s = (row.snapshot ?? {}) as Record<string, unknown>;
  const texte = (v: unknown) => (typeof v === 'string' && v.trim() !== '' ? v : undefined);
  const nombre = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
  return {
    id: row.article_id,
    slug: texte(s.slug),
    title: texte(s.title),
    excerpt: texte(s.excerpt),
    category: texte(s.category),
    date: texte(s.date),
    readingMinutes: nombre(s.readingMinutes),
    publisher: (s.publisher as NewsArticle['publisher']) ?? undefined,
    coverImage: (s.coverImage as NewsArticle['coverImage']) ?? undefined,
  };
}

export function useNewsFavorites() {
  const session = useUserStore((state) => state.session);
  const connecte = !!session;

  const [rows, setRows] = useState<FavoriteRow[]>([]);
  const [ids, setIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  /** Message d'échec transitoire, affiché puis effacé par l'écran. */
  const [erreur, setErreur] = useState(false);

  const recharger = useCallback(async () => {
    if (!connecte) {
      setRows([]);
      setIds(new Set());
      return;
    }
    setLoading(true);
    try {
      const lignes = await listFavorites();
      setRows(lignes);
      setIds(new Set(lignes.map((l) => l.article_id)));
    } catch {
      // Lecture impossible : on garde ce qu'on avait plutôt que de vider la liste sous les yeux.
    } finally {
      setLoading(false);
    }
  }, [connecte]);

  useEffect(() => {
    void recharger();
  }, [recharger]);

  const basculer = useCallback(
    async (article: NewsArticle) => {
      if (!connecte) return;
      const etaitFavori = ids.has(article.id);

      // ── Bascule immédiate, avant tout appel réseau ──
      setIds((actuels) => {
        const suivant = new Set(actuels);
        if (etaitFavori) suivant.delete(article.id);
        else suivant.add(article.id);
        return suivant;
      });
      setRows((actuelles) =>
        etaitFavori
          ? actuelles.filter((l) => l.article_id !== article.id)
          : [
              {
                article_id: article.id,
                snapshot: {
                  title: article.title,
                  excerpt: article.excerpt,
                  category: article.category,
                  date: article.date,
                  readingMinutes: article.readingMinutes,
                  slug: article.slug,
                  publisher: article.publisher,
                  coverImage: article.coverImage,
                },
                // Provisoire : la ligne réelle porte l'horodatage du serveur. Elle est en tête,
                // ce qui est bien sa place dans un tri par date décroissante.
                created_at: new Date().toISOString(),
              },
              ...actuelles,
            ],
      );

      try {
        if (etaitFavori) await removeFavorite(article.id);
        else await addFavorite(article);
      } catch {
        // ── ROLLBACK : on remet exactement l'état d'avant, puis on le signale ──
        setIds((actuels) => {
          const suivant = new Set(actuels);
          if (etaitFavori) suivant.add(article.id);
          else suivant.delete(article.id);
          return suivant;
        });
        setErreur(true);
        // On relit la vérité du serveur plutôt que de reconstituer la liste de mémoire : après un
        // échec, l'état local n'est plus une source fiable.
        void recharger();
      }
    },
    [connecte, ids, recharger],
  );

  return {
    connecte,
    rows,
    ids,
    loading,
    erreur,
    effacerErreur: useCallback(() => setErreur(false), []),
    basculer,
    recharger,
  };
}
